// Reusable image picker — upload from device (default) or paste a URL.
// Auto-resizes to 1200px max on longest side and compresses to JPEG (85% quality).
// Result is a base64 data URL stored directly on the model, or a plain URL string.
import { useState } from "react";

export default function ImagePicker({ value, onChange, label = "الصورة", height = "h-40" }) {
  const [mode, setMode] = useState(value?.startsWith("http") ? "url" : "upload");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (file) => {
    setErr("");
    if (!file) return;
    if (!file.type.startsWith("image/")) return setErr("يرجى اختيار ملف صورة صالح");
    if (file.size > 8 * 1024 * 1024) return setErr("الصورة كبيرة جداً (الحد الأقصى 8 ميغابايت)");
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = (e) => res(e.target.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = dataUrl;
      });
      const MAX = 1200;
      let { width, height: h } = img;
      if (Math.max(width, h) > MAX) {
        const scale = MAX / Math.max(width, h);
        width = Math.round(width * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, width, h);
      onChange(canvas.toDataURL("image/jpeg", 0.85));
    } catch { setErr("تعذّرت معالجة الصورة"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="image-picker">
      <div className="flex gap-1 bg-[var(--surface-alt)] p-1 rounded-lg text-xs">
        <button type="button" onClick={() => setMode("upload")}
                className={`flex-1 py-1.5 rounded-md font-black ${mode === "upload" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
                data-testid="ip-mode-upload">📷 من الجهاز</button>
        <button type="button" onClick={() => setMode("url")}
                className={`flex-1 py-1.5 rounded-md font-black ${mode === "url" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
                data-testid="ip-mode-url">🔗 لصق رابط</button>
      </div>

      {mode === "upload" ? (
        <label className="block w-full border-2 border-dashed border-[var(--border)] rounded-xl p-6 text-center cursor-pointer bg-[var(--surface-alt)] hover:border-primary-brand hover:bg-blue-50 transition"
               data-testid="ip-drop">
          <div className="text-3xl">📷</div>
          <div className="font-black text-sm mt-2" style={{fontFamily:"Cairo"}}>
            {busy ? "جاري معالجة الصورة..." : `اضغط لاختيار ${label} من جهازك`}
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1">JPG · PNG · WEBP — حتى 8 ميغابايت</div>
          {/* No `capture` attribute so the OS opens the photo picker/library
              instead of forcing the camera. Users can still pick a fresh
              camera shot from the picker's built-in "Camera" option. */}
          <input type="file" accept="image/*"
                 disabled={busy}
                 onChange={(e) => handleFile(e.target.files?.[0])}
                 className="hidden" data-testid="ip-file"/>
        </label>
      ) : (
        <input value={value?.startsWith("data:") ? "" : (value || "")}
               onChange={(e) => onChange(e.target.value)}
               placeholder="https://example.com/photo.jpg"
               className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none" dir="ltr"
               data-testid="ip-url"/>
      )}

      {err && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</div>}

      {value && (
        <div className="relative rounded-xl overflow-hidden border border-[var(--border)]">
          <img src={value} alt="preview" className={`w-full ${height} object-cover`} data-testid="ip-preview"/>
          <button type="button" onClick={() => onChange("")}
                  className="absolute top-2 left-2 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-lg hover:bg-red-600"
                  data-testid="ip-remove">× إزالة</button>
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded">
            {value.startsWith("data:") ? "📷 من الجهاز" : "🔗 رابط خارجي"}
          </div>
        </div>
      )}
    </div>
  );
}
